import React from 'react';
import { Table } from 'react-bootstrap';
import { ListItem, DragInfo } from '../../../types/files';
import { FileItem } from './FileItem';
import { SortableHeader } from './SortableHeader';
import { IndeterminateCheckbox } from './IndeterminateCheckbox';
import { SortField, SortDirection } from '../../../types/files';

interface FileListTableProps {
    items: ListItem[];
    selectedFiles: Set<string>;
    isLoading: boolean;
    isAdmin: boolean;
    dragInfo: DragInfo;
    sort: {
        field: SortField;
        direction: SortDirection;
    };
    onSort: (field: SortField) => void;
    onSelect: (itemId: string, event: React.MouseEvent<HTMLElement> | React.ChangeEvent<HTMLInputElement>) => void;
    onSelectAll: () => void;
    onDragStart: (e: React.DragEvent<HTMLTableRowElement>, itemId: string, itemType: 'file' | 'folder') => void;
    onDragOver: (e: React.DragEvent<HTMLTableRowElement>, itemId: string | null, itemType: 'file' | 'folder' | null) => void;
    onDrop: (e: React.DragEvent<HTMLTableRowElement>, targetId: string | null, targetType: 'file' | 'folder' | null, selectedFiles: Set<string>) => void;
    onDragEnd: (e: React.DragEvent<HTMLTableRowElement>) => void;
    onFolderClick: (folderId: string, folderName: string) => void;
    onContextMenu: (
        e: React.MouseEvent<HTMLDivElement>,
        id: string,
        name: string,
        type: 'folder' | 'file'
    ) => void;
}

export const FileListTable: React.FC<FileListTableProps> = ({
                                                                items,
                                                                selectedFiles,
                                                                isLoading,
                                                                isAdmin,
                                                                dragInfo,
                                                                sort,
                                                                onSort,
                                                                onSelect,
                                                                onSelectAll,
                                                                onDragStart,
                                                                onDragOver,
                                                                onDrop,
                                                                onDragEnd,
                                                                onFolderClick,
                                                                onContextMenu
                                                            }) => {
    const isAllSelected = items.length > 0 && selectedFiles.size === items.length;
    const isIndeterminate = selectedFiles.size > 0 && selectedFiles.size < items.length;

    const handleTableDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        onDragOver(e as any, null, null);
    };

    const handleTableDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        onDrop(e as any, null, null, selectedFiles);
    };

    return (
        <div
            onDragOver={handleTableDragOver}
            onDrop={handleTableDrop}
            className="file-list-container"
        >
            <Table hover responsive className="align-middle">
                <thead>
                <tr>
                    {isAdmin && (
                        <th className="checkbox-column">
                            <IndeterminateCheckbox
                                checked={isAllSelected}
                                indeterminate={isIndeterminate}
                                onChange={onSelectAll}
                                disabled={isLoading || items.length === 0}
                            />
                        </th>
                    )}
                    <SortableHeader
                        field="name"
                        currentSort={sort}
                        onSort={onSort}
                    >
                        Name
                    </SortableHeader>
                    <SortableHeader
                        field="size"
                        currentSort={sort}
                        onSort={onSort}
                    >
                        Size
                    </SortableHeader>
                    <SortableHeader
                        field="updated_at"
                        currentSort={sort}
                        onSort={onSort}
                    >
                        Last Modified
                    </SortableHeader>
                    <th>Source</th>
                    <th>Actions</th>
                </tr>
                </thead>
                <tbody>
                {items.length === 0 ? (
                    <tr>
                        <td colSpan={isAdmin ? 6 : 5} className="text-center">
                            No items found
                        </td>
                    </tr>
                ) : (
                    items.map(item => (
                        <FileItem
                            key={item.uniqueId}
                            item={item}
                            isSelected={selectedFiles.has(item.id)}
                            isLoading={isLoading}
                            isAdmin={isAdmin}
                            dragInfo={dragInfo}
                            onSelect={onSelect}
                            onDragStart={(e) => onDragStart(e, item.id, item.type)}
                            onDragOver={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                onDragOver(e, item.id, item.type);
                            }}
                            onDrop={async (e, targetId) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (item.type === 'folder') {
                                    await onDrop(e, targetId, 'folder', selectedFiles);
                                } else {
                                    await onDrop(e, null, null, selectedFiles);
                                }
                            }}
                            onDragEnd={onDragEnd}
                            onFolderClick={onFolderClick}
                            onContextMenu={onContextMenu}
                        />
                    ))
                )}
                </tbody>
            </Table>
        </div>
    );
};

export default FileListTable;